import type {
	CSMSDK as RovoWidgetSDK,
	CSMSDKQueue as RovoWidgetSDKQueue,
} from '@atlassian/customer-service-management-widget-sdk-types';

import type { Surface } from './types';

declare global {
	interface Window {
		ROVO_WIDGET?: RovoWidgetSDK | RovoWidgetSDKQueue;
		ROVO_WIDGET_CLEANUP?: () => void;
		ROVO_WIDGET_REINIT?: () => void;
	}
}

let promise: Promise<RovoWidgetSDK> | null = null;

const SCRIPT_ID = 'rovo-service-agent-widget-script';

export const createSDKQueue = (): RovoWidgetSDKQueue => {
	const q: RovoWidgetSDKQueue = function WidgetQueue(...args) {
		q.__q__.push(args);
	};
	q.__q__ = [];
	return q;
};

// Helper function to validate environment
export const validateEnvironment = (): void => {
	if (typeof window === 'undefined') {
		throw new Error('Failed to load CSM SDK: window is undefined');
	}

	// Upgrading @atlaskit/platform; fix these violations.
	// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
	const body = document.querySelector('body');
	if (!body) {
		throw new Error('Document body not found');
	}
};

// Helper function to initialize SDK queue with callbacks
export const initializeSDKQueue = (onOpen?: () => void, onClose?: () => void): void => {
	if (typeof window.ROVO_WIDGET === 'undefined') {
		window.ROVO_WIDGET = createSDKQueue();
		if (onOpen) {
			window.ROVO_WIDGET('onOpen', onOpen);
		}
		if (onClose) {
			window.ROVO_WIDGET('onClose', onClose);
		}
		window.ROVO_WIDGET('hide');
	}
};

// Helper function to open the widget and send an initial message
export const openWidgetWithMessage = (message: string): void => {
	if (window.ROVO_WIDGET) {
		window.ROVO_WIDGET('show');
		window.ROVO_WIDGET('open');
		window.ROVO_WIDGET('createNewConversation', { message });
	}
};

// Helper function to check if SDK is already loaded
export const isSDKLoaded = (): boolean => {
	return !!(window.ROVO_WIDGET && !('__q__' in window.ROVO_WIDGET));
};

// Helper function to create script URL
export const createScriptURL = (
	siteId: string,
	widgetId: string,
	surface: Surface,
	locale: string,
): string => {
	const site = window.location.hostname;
	const src = new URL(`https://${site}/rovo/widget/script.js`);
	src.searchParams.set('siteId', siteId);
	src.searchParams.set('site', site);
	src.searchParams.set('widgetId', widgetId);
	src.searchParams.set('surface', surface);
	src.searchParams.set('locale', locale);
	return src.toString();
};

// Helper function to handle script load event
export const handleScriptLoad = (
	resolve: (value: RovoWidgetSDK) => void,
	reject: (reason?: Error) => void,
): void => {
	if (isSDKLoaded()) {
		resolve(window.ROVO_WIDGET as RovoWidgetSDK);
	} else if (window.ROVO_WIDGET && '__q__' in window.ROVO_WIDGET) {
		reinitializeRovoWidgetSDKScript();
		if (isSDKLoaded()) {
			resolve(window.ROVO_WIDGET as unknown as RovoWidgetSDK);
		} else {
			promise = null;
			reject(new Error('SDK failed to initialize properly - still in queue mode'));
		}
	} else {
		promise = null;
		reject(new Error('Failed to load ROVO_WIDGET SDK script: window.ROVO_WIDGET is undefined'));
	}
};

// Helper function to handle script error event
export const handleScriptError = (reject: (reason?: Error) => void): void => {
	promise = null;
	reject(new Error('Failed to load ROVO_WIDGET SDK script: onerror'));
};

// Helper function to create and append script element
export const createAndAppendScript = (
	scriptURL: string,
	resolve: (value: RovoWidgetSDK) => void,
	reject: (reason?: Error) => void,
): void => {
	// Upgrading @atlaskit/platform; fix these violations.
	// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
	const script = document.createElement('script');
	script.async = true;
	script.src = scriptURL;
	script.id = SCRIPT_ID;

	script.onerror = () => handleScriptError(reject);
	script.onload = () => handleScriptLoad(resolve, reject);

	// Upgrading @atlaskit/platform; fix these violations.
	// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
	const body = document.querySelector('body');
	body?.appendChild(script);
};

export const reinitializeRovoWidgetSDKScript = (): void => {
	if (window.ROVO_WIDGET_REINIT) {
		window.ROVO_WIDGET_REINIT();
	}
};

export const loadRovoWidgetSDKScript = ({
	siteId,
	widgetId,
	surface,
	locale,
	onOpen,
	onClose,
}: {
	locale: string;
	onClose?: () => void;
	onOpen?: () => void;
	siteId: string;
	surface: Surface;
	widgetId: string;
}): Promise<RovoWidgetSDK> => {
	if (!promise) {
		promise = new Promise<RovoWidgetSDK>((resolve, reject) => {
			try {
				validateEnvironment();
				initializeSDKQueue(onOpen, onClose);
				// If SDK is already loaded (not a queue), resolve immediately
				if (isSDKLoaded()) {
					resolve(window.ROVO_WIDGET as RovoWidgetSDK);
					return;
				}

				const scriptURL = createScriptURL(siteId, widgetId, surface, locale);
				createAndAppendScript(scriptURL, resolve, reject);
			} catch (error) {
				reject(error);
			}
		});
	}

	return promise;
};

export const cleanupRovoWidgetSDKScript = (): void => {
	// Upgrading @atlaskit/platform; fix these violations.
	// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
	const scriptElement = document.getElementById(SCRIPT_ID);
	if (scriptElement) {
		// Upgrading @atlaskit/platform; fix these violations.
		// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
		document.body.removeChild(scriptElement);
	}

	if (window.ROVO_WIDGET_CLEANUP) {
		window.ROVO_WIDGET_CLEANUP();
	}

	if (window.ROVO_WIDGET) {
		delete window.ROVO_WIDGET;
	}

	// Reset the tracking variables
	promise = null;
};
